/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponents.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import VehicleComponentsAddDialog from "../../components/labcar/VehicleComponentsAddDialog";
import { HandleApiActions, labCar } from "../../redux/actions";
import actionType from "../../redux/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import { Search } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { archiveReasons, PROJECT_STATUS } from "../../config/appConstants";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { componentStyle } from "../../config/appTheme";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import { showToastMessage } from "../../redux/actions/actionHandler";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import VehicleComponentsEditDialog from "../../components/labcar/VehicleComponentsEditDialog";

function VehicleComponents({
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  selectedZoneDetails,
  supplierOptionsList,
  vcOptionsList,
  filterState,
  onFilterChange,
  onClearFilters,
  selectedVehicleSystem,
  selectedArchivedVCs,
  setSelectedArchivedVCs,
  selectedArchivedVSs,
  viewArchived,
  clearAllCounter,
  retrievalRefreshCounter,
}) {
  const dispatch = useDispatch();
  const reduxProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const projectDetails = selectedProjectDetails || reduxProjectDetails;
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  const vehicleComponents = useSelector(
    (state) => state.labCarReducer.vehicleComponents,
  );
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [copyVehicleComponentDetails, setCopyVehicleComponentDetails] =
    useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState("name");
  const [searchFieldValue, setSearchFieldValue] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [openRecreationVCDialog, setOpenRecreationVCDialog] = useState(false);
  const [selectedParentVCDetails, setSelectedParentVCDetails] = useState(null);
  const [recreationParentVCList, setRecreationParentVCList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [openVCChangeLogsDialog, setOpenVCChangeLogsDialog] = useState(false);
  const [vcChangeLogsList, setVCChangeLogsList] = useState([]);
  const [vcChangeLogsTotalCount, setVCChangeLogsTotalCount] = useState(0);
  const [vcLogsDialogData, setVCLogsDialogData] = useState(null);
  const [vcDetailsDialogTabs, setVcDetailsDialogTabs] = useState([
    "Details",
    "Log",
    "Attribute",
  ]);
  const [openEditVCDialog, setOpenEditVCDialog] = useState(false);
  const [selectedVCItemForEdit, setSelectedVCItemForEdit] = useState(null);
  const [confirmArchiveDialogOpen, setConfirmArchiveDialogOpen] =
    useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);

  const handleArchivedVCCheckboxChange = (vcId, checked) => {
    if (checked) {
      setSelectedArchivedVCs((prev) => [...(prev || []), vcId]);
    } else {
      setSelectedArchivedVCs((prev) => (prev || []).filter((id) => id !== vcId));
    }
  };

  const getParentVCDetails = async () => {

    let parentVCResponse = await Promise.resolve(dispatch(
      HandleApiActions({
        ...labCar.get_parent_vc,
        params: {
          project_id: selectedProjectDetails.project_id,
          parent_project_id: selectedProjectDetails.parent_project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
          parent_variant_id:
            selectedSingleVariantDetails?.parent_variant_details?.variant_id,
          vs_id: selectedVehicleSystem.id,
          parent_vs_id: selectedVehicleSystem?.parent_vs_id,
        },
      }),
    ));
    if (parentVCResponse.error) {
      showToastMessage({
        type: "error",
        message: parentVCResponse.msg,
      });
      setRecreationParentVCList([]);
      setSelectedParentVCDetails(null);
    } else {
      setSelectedParentVCDetails(parentVCResponse.data[0]);
      setRecreationParentVCList(parentVCResponse.data);
      setOpenRecreationVCDialog(true);
    }
  };

  const handleVCRecreateOpen = async () => {
    await getParentVCDetails();
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveItemId(item?.id);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };
  const handleGetVehicleComponentChangeLogs = async ({
    pageNo = 1,
    pageSize = 25,
  } = {}) => {
    const entityId = vcLogsDialogData?.id;
    if (!entityId) return;
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...labCar.vehicle_component_logs,
        params: {
          entity_id: Number(entityId),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      }),
    ));
    if (resp?.error) {
      setVCChangeLogsList([]);
      setVCChangeLogsTotalCount(0);
      return;
    }
    setVCChangeLogsList(resp?.data || []);
    setVCChangeLogsTotalCount(resp?.total_count || 0);
  };
  const handleVCLogsDialogOpen = (item) => {
    setVCLogsDialogData(item);
    setOpenVCChangeLogsDialog(true);
  };
  const handleVCViewDialogOpen = (item) => {
    setVCLogsDialogData(item);
    setVcDetailsDialogTabs(["Details", "Attribute"]);
    setOpenVCChangeLogsDialog(true);
  };
  const handleVCLogsDialogClose = () => {
    setOpenVCChangeLogsDialog(false);
    setVCLogsDialogData(null);
    setVCChangeLogsList([]);
    setVCChangeLogsTotalCount(0);
    setVcDetailsDialogTabs(["Details", "Log", "Attribute"]);
  };
  const handleVehicleComponentCopyOpen = async () => {
    if (!vcLogsDialogData) return;

    const vcDetails = await getVehicleComponentDetailsForEdit(vcLogsDialogData);
    setCopyVehicleComponentDetails(vcDetails);
    setOpenVCChangeLogsDialog(false);
    setOpenAddDialog(true);
  };
  const getVehicleComponentDetailsForEdit = async (item) => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const vsId = selectedVehicleSystemId;
    const vcId = item?.id;

    if (!projectId || !variantId || !vsId || !vcId) return item;

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...labCar.get_vehicle_components_all,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          vs_id: Number(vsId),
          vc_id: Number(vcId),
        },
      }),
    ));

    if (resp?.error) return item;
    const matchedComponent = (resp?.data || []).find(
      (vc) =>
        Number(vc?.id) === Number(vcId) || Number(vc?.vc_id) === Number(vcId),
    );
    return matchedComponent || item;
  };

  const checkIsVehicleSystemNotActive = (selectedVehicleSystem, item) => {
    const isStatusArchived = (val) =>
      typeof val === "string" && val.toUpperCase() === "ARCHIVED";

    if (
      isStatusArchived(selectedVehicleSystem?.archive_status) ||
      isStatusArchived(selectedVehicleSystem?.archive_reason) ||
      isStatusArchived(item?.vs_details?.archive_status) ||
      isStatusArchived(item?.vehicle_system_details?.archive_status) ||
      isStatusArchived(item?.vs_archive_status)
    ) {
      return true;
    }

    if (
      Array.isArray(selectedVehicleSystem?.archive_reason) &&
      selectedVehicleSystem.archive_reason.some(
        (r) => typeof r === "string" && r.toUpperCase() === "ARCHIVED",
      )
    ) {
      return true;
    }

    if (
      typeof selectedVehicleSystem?.status === "string" &&
      ["ARCHIVED", "INACTIVE"].includes(
        selectedVehicleSystem.status.toUpperCase(),
      )
    ) {
      return true;
    }

    if (selectedVehicleSystem?.is_active === false) {
      return true;
    }

    if (
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some((r) => {
        const str = String(r).toUpperCase();
        return (
          str.includes("VS_ARCHIVED") ||
          str.includes("VEHICLE_SYSTEM_ARCHIVED")
        );
      })
    ) {
      return true;
    }

    return false;
  };

  const getVCArchiveTooltipTitle = (missingArchiveReasons, isVehicleSystemNotActive) => {
    const displayMissingReasons = missingArchiveReasons.filter(
      (r) =>
        !String(r).toUpperCase().includes("VS_ARCHIVED") &&
        !String(r).toUpperCase().includes("VEHICLE_SYSTEM_ARCHIVED"),
    );
    let displayReasons = [];
    if (displayMissingReasons.length > 0) {
      displayReasons = displayMissingReasons;
    } else if (!isVehicleSystemNotActive && missingArchiveReasons.length > 0) {
      displayReasons = missingArchiveReasons;
    }

    return [
      displayReasons.length > 0 ? `Missing ${displayReasons.join(", ")}` : "",
      isVehicleSystemNotActive ? "Parent vehicle system is not active" : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const renderVCArchiveErrorIcon = (item) => {
    const hasMissingArchiveReasons =
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some(
        (r) => !archiveReasons?.vc_exceptionToActive?.includes(r),
      );
    const missingArchiveReasons = hasMissingArchiveReasons
      ? item.archive_reason.filter(
          (r) => !archiveReasons?.vc_exceptionToActive?.includes(r),
        )
      : [];

    const isVehicleSystemNotActive = checkIsVehicleSystemNotActive(
      selectedVehicleSystem,
      item,
    );

    if (!hasMissingArchiveReasons && !isVehicleSystemNotActive) return null;

    const tooltipTitle = getVCArchiveTooltipTitle(
      missingArchiveReasons,
      isVehicleSystemNotActive,
    );

    return (
      <CustomToolTip type="component" title={tooltipTitle}>
        <ErrorOutlineIcon sx={{ fontSize: "18px", color: "red", mr: 0.5 }} />
      </CustomToolTip>
    );
  };

  const handleMenuAction = async (action, item) => {
    handleCloseMenu();
    if (action === "edit") {
      const vcDetails = await getVehicleComponentDetailsForEdit(item);
      setSelectedVCItemForEdit(vcDetails);
      setOpenEditVCDialog(true);
      return;
    }
    if (action === "view") {
      handleVCViewDialogOpen(item);
      return;
    }
    if (action === "log") {
      setVcDetailsDialogTabs(["Log"]);
      handleVCLogsDialogOpen(item);
      return;
    }
    if (action === "archive") {
      setItemToArchive(item);
      setConfirmArchiveDialogOpen(true);
    }
  };

  const handleConfirmArchiveVC = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const item = itemToArchive;
    if (!projectId || !variantId || !item?.id) {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
      return;
    }

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.archive_vehicle_component,
            params: {
              vc_ids: [item.id],
              project_id: Number(projectId),
              variant_id: Number(variantId),
            },
            show_toast: true,
          }),
        ),
      );
      if (!res?.error) {
        getVehicleComponents().catch(console.error);
      }
    } catch (err) {
      console.error("Error archiving vehicle component:", err);
    } finally {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
    }
  };

  const getVehicleComponents = React.useCallback(async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = selectedProjectDetails?.is_zonal;
    const zonePosition = selectedZoneDetails?.position;
    const vsId = selectedVehicleSystemId;
    if (!projectId || !variantId || !vsId) {
      setTotalCount(0);
      dispatch({
        type: actionType.GET_VEHICLE_COMPONENTS,
        payload: { data: [], total_count: 0 },
      });
      return;
    }
    if (isZonal && (zonePosition === null || zonePosition === undefined))
      return;
    const apiDetails = labCar.get_vehicle_components;
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          vs_id: Number(vsId),
          ...(isZonal && { zone_position: zonePosition }),
          ...(searchFieldValue && {
            [selectedSearchField]: searchFieldValue,
          }),
          sort_field: filterState.sortField,
          sort_order: filterState.sortOrder,
          ...(filterState.nameFilterSearch && {
            name: filterState.nameFilterSearch,
          }),
          ...(filterState.componentNumFilterSearch && {
            component_number: filterState.componentNumFilterSearch,
          }),
          ...(filterState.descriptionFilterSearch && {
            description: filterState.descriptionFilterSearch,
          }),
          page_number: page,
          page_size: rowsPerPage,
          archive: !!viewArchived,
        },
      }),
    ));
    if (resp?.error) {
      setTotalCount(0);
      dispatch({
        type: actionType.GET_VEHICLE_COMPONENTS,
        payload: { data: [], total_count: 0 },
      });
      return;
    }

    setTotalCount(Number(resp?.total_count ?? 0));
  }, [
    dispatch,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.is_zonal,
    selectedZoneDetails?.position,
    selectedVehicleSystemId,
    selectedSearchField,
    searchFieldValue,
    filterState.sortField,
    filterState.sortOrder,
    filterState.nameFilterSearch,
    filterState.componentNumFilterSearch,
    filterState.descriptionFilterSearch,
    page,
    rowsPerPage,
    viewArchived,
  ]);

  useEffect(() => {
    getVehicleComponents().catch(console.error);
  }, [
    page,
    rowsPerPage,
    selectedSearchField,
    searchFieldValue,
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedZoneDetails,
    selectedZoneDetails,
    filterState.sortField,
    filterState.sortOrder,
    filterState.nameFilter,
    filterState.componentNumFilter,
    filterState.descriptionFilter,
    filterState.nameFilterSearch,
    filterState.componentNumFilterSearch,
    filterState.descriptionFilterSearch,
    selectedVehicleSystemId,
    getVehicleComponents,
  ]);

  useEffect(() => {
    if (retrievalRefreshCounter > 0) {
      getVehicleComponents().catch(console.error);
    }
  }, [retrievalRefreshCounter, getVehicleComponents]);

  useEffect(() => {
    setSelectedSearchField("name");
    setSearchFieldValue("");
    setPage(1);
  }, [
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedVehicleSystemId,
  ]);




  const handlePageChange = (value) => setPage(value);
  const [nameSearch, setNameSearch] = useState("");
  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (nameSearch.length >= 3 || nameSearch.length === 0) {
      debounceRef.current = setTimeout(() => {
        setSelectedSearchField("name");
        setSearchFieldValue(nameSearch);
        setPage(1);
      }, 500);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [nameSearch]);

  return (
    <Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "450px",
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={"Vehicle Components"}
                showRecreateIcon={
                  Boolean(
                    selectedProjectDetails?.parent_project_id &&
                    selectedSingleVariantDetails?.parent_variant_details?.variant_id &&
                    selectedVehicleSystem?.parent_vs_id
                  )
                }
                onRecreateClick={handleVCRecreateOpen}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search Name"
                    variant="outlined"
                    fullWidth
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={componentStyle.searchField.textField}
                  />
                  <Box>
                    {!viewArchived && (
                      <AddButtonV2
                        handleOnClick={(e) => setOpenAddDialog(true)}
                        disabled={!selectedVehicleSystemId}
                      />
                    )}
                  </Box>
                </Box>
              </VlcCardHeader>
            }
          />
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {selectedVehicleSystemId &&
            vehicleComponents &&
            vehicleComponents?.length > 0 ? (
              vehicleComponents?.map((item, index) => (
                <VlcCardContent key={item.id} onClick={() => {}} data={item}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "stretch",
                    }}
                  >
                    {item?.archive_status?.toUpperCase() === "ARCHIVED" && (
                        <Box 
                          sx={{ 
                            p: 1, 
                            pb: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }} 
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CustomCheckBoxV2
                            noPaddingRequired={true}
                            key={`vc-${item.id}-${clearAllCounter}`}
                            value={selectedArchivedVCs?.includes(item.id)}
                            disabled={selectedArchivedVSs?.length > 0}
                            onChange={(e) => handleArchivedVCCheckboxChange(item.id, e.target.checked)}
                          />
                        </Box>
                      )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        p: 1,
                        width: "90%",
                      }}
                    >
                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            fontWeight: "bold",
                          }}
                          title={`Component No: ${item?.component_number ?? ""}`}
                        >
                          {`Component No: ${item?.component_number ?? ""}`}
                        </CustomToolTip>
                      </Typography>

                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Name: ${item?.name ?? ""}`}
                        >
                          {`Name: ${item?.name ?? ""}`}
                        </CustomToolTip>
                      </Typography>

                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Description: ${item?.description ?? ""}`}
                        >
                          {`Description: ${item?.description ?? ""}`}
                        </CustomToolTip>
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1, pt: 1, display: "flex", alignItems: "center" }}>
                      {renderVCArchiveErrorIcon(item)}
                      <IconButton
                        size="small"
                        sx={{ color: "black" }}
                        onClick={(e) => handleMoreClick(e, item)}
                      >
                        <MoreVertIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && activeItemId === item?.id}
                        onClose={handleCloseMenu}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: 0,
                              boxShadow: 1,
                            },
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => handleMenuAction("view", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          View
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleMenuAction("edit", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Edit details
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleMenuAction("log", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Log
                        </MenuItem>
                        {item?.archive_status?.toUpperCase() !== "ARCHIVED" &&
                          projectDetails?.status === PROJECT_STATUS.DRAFT &&
                          Boolean(projectDetails?.archive_vlc) && (
                          <MenuItem
                            onClick={() => handleMenuAction("archive", item)}
                            sx={{ ...componentStyle.font.cardText }}
                          >
                            Archive
                          </MenuItem>
                        )}
                      </Menu>
                    </Box>
                  </Box>
                </VlcCardContent>
              ))
            ) : (
              <Typography variant="body1" sx={styles.noDataAvailable}>
                No data available
              </Typography>
            )}
          </CardContent>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: "40px",
              borderTop: "1px solid #ddd",
              borderRadius: "0px !important",
            }}
          >
            {(totalCount || vehicleComponents?.length || 0) > 0 && (
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                totalItems={Math.max(
                  totalCount || 0,
                  vehicleComponents?.length || 0,
                )}
              />
            )}
          </Paper>
        </Card>
      </Box>
      <VehicleComponentsAddDialog
        openDialog={openAddDialog}
        setOpenDialog={setOpenAddDialog}
        title={"Add Vehicle Component"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        supplierOptionsList={supplierOptionsList}
        vcOptionsList={vcOptionsList}
        getVehicleComponents={getVehicleComponents}
        copyVehicleComponentDetails={copyVehicleComponentDetails}
        onCopyPrefillClear={() => setCopyVehicleComponentDetails(null)}
      />

      <VehicleComponentsAddDialog
        openDialog={openRecreationVCDialog}
        setOpenDialog={setOpenRecreationVCDialog}
        title={"Recreate Vehicle Component"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        supplierOptionsList={supplierOptionsList}
        vcOptionsList={vcOptionsList}
        getVehicleComponents={getVehicleComponents}
        recreationMode={true}
        recreationParentVCList={recreationParentVCList}
        selectedParentVCDetails={selectedParentVCDetails}
        setSelectedParentVCDetails={setSelectedParentVCDetails}
      />
      <VehicleComponentsEditDialog
        openDialog={openEditVCDialog}
        setOpenDialog={setOpenEditVCDialog}
        title={viewArchived ? "Draft Vehicle Component" : "Edit Vehicle Component"}
        viewArchived={viewArchived}
        selectedVCItem={selectedVCItemForEdit}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vcOptionsList={vcOptionsList}
        selectedVehicleSystemId={selectedVehicleSystemId}
        selectedVehicleSystemDetails={selectedVehicleSystem}
        getVehicleComponents={getVehicleComponents}
      />
      <CustomDialogBoxV2
        open={openVCChangeLogsDialog}
        onClose={handleVCLogsDialogClose}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap:2,
              width: "100%",
            }}
          >
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Vehicle Components
            </Typography>
            <IconButton
              size="small"
              onClick={handleVehicleComponentCopyOpen}
              disabled={!vcLogsDialogData}
              aria-label="Copy Vehicle Component"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={vcLogsDialogData}
            ChangeLogsList={vcChangeLogsList}
            getLogsData={handleGetVehicleComponentChangeLogs}
            selectedComponent={vcLogsDialogData}
            zone_name={vcLogsDialogData?.zone_details?.zone_name}
            variantOptionList={variantOptionList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            openEditDetailsDialog={openVCChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenVCChangeLogsDialog}
            totalLogsCount={vcChangeLogsTotalCount}
            type={"VC"}
            visibleTabs={vcDetailsDialogTabs}
          />
        </Box>
      </CustomDialogBoxV2>

      {confirmArchiveDialogOpen && (
        <CustomDialogBoxV2
          open={confirmArchiveDialogOpen}
          onClose={() => {
            setConfirmArchiveDialogOpen(false);
            setItemToArchive(null);
          }}
          title="Confirm Changes"
          // dialogRadius="0rem"
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmArchiveVC}
          onClickReject={() => {
            setConfirmArchiveDialogOpen(false);
            setItemToArchive(null);
          }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
            <Typography>
               This change will affect the downstream intersections, if any are linked to the element.
            </Typography>
            <Typography>
              Are you sure you want to proceed?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

VehicleComponents.propTypes = {
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.any,
    parent_project_id: PropTypes.any,
    is_zonal: PropTypes.any,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.any,
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.any,
    }),
  }),
  variantOptionList: PropTypes.array,
  selectedZoneDetails: PropTypes.shape({
    position: PropTypes.any,
  }),
  supplierOptionsList: PropTypes.array,
  vcOptionsList: PropTypes.array,
  filterState: PropTypes.shape({
    sortField: PropTypes.any,
    sortOrder: PropTypes.any,
    nameFilterSearch: PropTypes.any,
    componentNumFilterSearch: PropTypes.any,
    descriptionFilterSearch: PropTypes.any,
    nameFilter: PropTypes.any,
    componentNumFilter: PropTypes.any,
    descriptionFilter: PropTypes.any,
  }),
  onFilterChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  selectedVehicleSystem: PropTypes.shape({
    id: PropTypes.any,
    parent_vs_id: PropTypes.any,
    archive_status: PropTypes.string,
    archive_reason: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    status: PropTypes.string,
    is_active: PropTypes.bool,
  }),
  selectedArchivedVCs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedVCs: PropTypes.func,
  selectedArchivedVSs: PropTypes.arrayOf(PropTypes.any),
  viewArchived: PropTypes.bool,
  clearAllCounter: PropTypes.number,
  retrievalRefreshCounter: PropTypes.number,
};

export default VehicleComponents;

const styles = {
  vsContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: "350px", sm: "400px", md: "500px" },
    maxHeight: { xs: "500px", sm: "600px", md: "700px" },
    width: "100%",
    display: "flex",
    flexDirection: "column", // changed from "row" to "column"
    alignItems: "stretch",
    justifyContent: "stretch",
    height: "100%", // ensure full height
    boxSizing: "border-box",
  },
  card: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  cardContentCards: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    border: "1px solid lightgrey",
  },
  cardContentCardContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
};
